import { useState, useEffect, useRef, memo } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { FaAngleRight, FaAngleDown, FaChartPie, FaUsers, FaCogs, FaUser, FaComments, FaRobot } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
// Removed usePopup import - showPopup now passed as prop
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { loadPolygonMaskPlugin } from "tsparticles-plugin-polygon-mask";
import ClearChatConfirmation from "./ClearChatConfirmation";

const Sidebar = ({ categories, isOpen, setIsOpen, messages, clearChatHistory, isMobile, currentOrientation, showPopup }) => {
  // INVESTIGATION: Log when Sidebar re-renders and WHY
  // console.log("🔍 SIDEBAR RE-RENDER at", new Date().toLocaleTimeString());
  // console.log("  📋 categories:", categories?.length);
  // console.log("  🚪 isOpen:", isOpen);
  // console.log("  📱 isMobile:", isMobile);
  // console.log("  💬 messages:", messages?.length);
  // console.log("  🎯 currentOrientation:", currentOrientation);
  // console.log("  📞 showPopup:", typeof showPopup);

  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const [isChattyExpanded, setIsChattyExpanded] = useState(false); // Keep for potential future use
  const [isDashboardExpanded, setIsDashboardExpanded] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // DECOUPLED ANIMATION STATE - Survives re-renders
  const particlesRef = useRef({ show: false, opacity: 0.1 });
  const animationTimersRef = useRef({ timer1: null, timer2: null });
  // eslint-disable-next-line no-unused-vars
  const [particleForceUpdate, setParticleForceUpdate] = useState(0); // Reserved for future particle force updates

  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  // DECOUPLED ANIMATION - Only responds to sidebar open/close
  useEffect(() => {
    // Clear existing timers
    const timers = animationTimersRef.current;
    clearTimeout(timers.timer1);
    clearTimeout(timers.timer2);

    if (isOpen) {
      // First wait for sidebar to fully expand before showing particles
      timers.timer1 = setTimeout(() => {
        // Update animation state without triggering re-render
        particlesRef.current = { show: true, opacity: 0.2 };
        setParticleForceUpdate(prev => prev + 1); // Force single update

        // Then start fading in the particles to full opacity
        timers.timer2 = setTimeout(() => {
          particlesRef.current = { show: true, opacity: 1.0 };
          setParticleForceUpdate(prev => prev + 1); // Force single update
        }, 200); // Small delay after particles appear

      }, 500); // Wait for sidebar expansion to complete
    } else {
      // Immediately start fade out
      particlesRef.current = { show: true, opacity: 0.2 };
      setParticleForceUpdate(prev => prev + 1);

      // Then remove particles from DOM after fade completes
      timers.timer1 = setTimeout(() => {
        particlesRef.current = { show: false, opacity: 0.1 };
        setParticleForceUpdate(prev => prev + 1);
      }, 700); // Wait for fade-out to complete
    }

    return () => {
      clearTimeout(timers.timer1);
      clearTimeout(timers.timer2);
    };
  }, [isOpen]); // ONLY isOpen dependency - decoupled from external state

  // tsParticles initialization function
  const particlesInit = async (engine) => {
    await loadSlim(engine);
    await loadPolygonMaskPlugin(engine);
  };

  // Clear chat history confirmation handlers - kept for ClearChatConfirmation component
  const handleClearConfirm = async () => {
    try {
      await clearChatHistory();
      setShowClearConfirm(false);
      showPopup('All chat history has been successfully deleted', 'success', { duration: 3000 });
    } catch (error) {
      console.error('Failed to clear chat history:', error);
      showPopup('Failed to clear chat history. Please try again.', 'error', { duration: 3000 });
    }
  };

  const handleClearCancel = () => {
    setShowClearConfirm(false);
  };

  // Match the reference configuration more closely
  const particlesOptions = {
    fpsLimit: 30,
    fullScreen: {
      enable: false,
    },
    background: {
      color: "transparent",
    },
    interactivity: {
      events: {
        onHover: {
          enable: true,
          mode: "grab"
        }
      },
      modes: {
        bubble: {
          distance: 60,
          duration: 2,
          opacity: 0.6,
          size: 12,
          speed: 3
        }
      }
    },
    particles: {
      color: {
        value: "#34ebd5", // Green color
        animation: {
          enable: true,
          speed: 20,
          sync: true
        }
      },
      links: {
        blink: false,
        color: "random", // Random colored links
        consent: false,
        distance: 350,
        enable: true,
        opacity: 0.2,
        width: 1
      },
      move: {
        enable: true,
        outModes: "bounce",
        speed: { min: 0.5, max: 1 }
      },
      number: {
        value: 30 // Reduced for sidebar
      },
      opacity: {
        animation: {
          enable: true,
          speed: 2,
          sync: false
        },
        random: false,
        value: { min: 1, max: 5 }
      },
      shape: {
        type: "circle"
      },
      size: {
        animation: {
          enable: true,
          speed: 10,
          sync: false
        },
        random: true,
        value: { min: 0.1, max: 1 }
      }
    },
    polygon: {
      draw: {
        enable: false,
        stroke: {
          color: "#fff",
          width: 0.3,
          opacity: 0.2
        }
      },
      position: {
        x: 20,
        y: 0
      },
      move: {
        radius: 80
      },
      inline: {
        arrangement: "equidistant"
      },
      scale: 1,
      type: "inline",
      url: "/NW.svg"
    },
    detectRetina: true
  };

  // REMOVED: The resize handler that was overriding App.js
  // This was causing the issue with the sidebar toggle

  return (
  <aside
    className={`p-4 transition-all fixed left-0 top-0 bottom-0 h-[100vh] flex flex-col justify-start pt-16 z-[60] overflow-hidden
  ${
    isMobile && currentOrientation === 'portrait' 
      ? (isOpen ? 'w-64' : 'w-0 opacity-0')                    // Portrait: fully hide with transition
      : isMobile && currentOrientation === 'landscape'
      ? (isOpen ? 'w-64' : 'w-0 opacity-0')                    // Mobile landscape: toggleable like portrait
      : (isOpen ? 'w-64' : 'w-10')                             // Desktop: collapsed to thin bar
  }
    `}
    style={{ 
      backgroundColor: 'var(--bg-primary)',
      boxShadow: 'var(--shadow-sidebar)'
    }}
  >
      {/* Only render particles when sidebar is expanded AND animation delay has passed */}
      {/* DECOUPLED Particles Effect - Immune to external re-renders */}
      {particlesRef.current.show && (
        <div
          className="absolute inset-0 z-0 transition-opacity duration-900 ease-in"
          style={{ opacity: particlesRef.current.opacity }}
        >
          <Particles
            id="tsparticles"
            init={particlesInit}
            options={particlesOptions}
            className="absolute inset-0"
          />
        </div>
      )}

      {/* Expand/Collapse Button */}
      {!(isMobile && currentOrientation === 'portrait') && (
  <button
    onClick={() => setIsOpen(!isOpen)}
    className="absolute top-[4.5rem] left-full transform -translate-x-full -translate-y-1/2
      w-6 h-6 flex items-center justify-center 
      rounded-l transition-all duration-300 z-10"
    style={{
      backgroundColor: 'var(--bg-secondary)',
      color: 'var(--text-primary)',
      boxShadow: 'var(--shadow-toggle)'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
      e.currentTarget.style.opacity = '0.9';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
      e.currentTarget.style.opacity = '1';
    }}
  >
    {isOpen ? (
      <FiChevronLeft size={18} style={{ color: 'var(--text-primary)' }} />
    ) : (
      <FiChevronRight size={18} style={{ color: 'var(--text-primary)' }} />
    )}
  </button>
)}


      <ul className="mt-10 space-y-2 transition-all relative z-10 w-full flex-grow">
        {/* Dashboard Section */}
        {user && (
        <li className={`${isOpen ? "opacity-100 w-full" : "hidden pointer-events-none"}`}>
          <div className="flex justify-between items-center w-full px-3 py-2 rounded-md cursor-pointer transition"
            style={{ 
              color: 'var(--text-primary)',
              backgroundColor: isDashboardExpanded ? 'var(--bg-secondary)' : 'transparent'
            }}
            onMouseEnter={(e) => {
              if (!isDashboardExpanded) {
                e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isDashboardExpanded) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}>
            <button
              onClick={() => navigate(isAdmin ? "/admin-dashboard" : "/dashboard")}
              className="flex-grow text-left flex items-center"
            >
              <FaChartPie className="mr-2" />
              Dashboard
            </button>
            <button
              className="p-1 rounded"
              onClick={(e) => {
                e.stopPropagation();
                setIsDashboardExpanded(!isDashboardExpanded);
              }}
            >
              {isDashboardExpanded ? <FaAngleDown size={16} /> : <FaAngleRight size={16} />}
            </button>
          </div>
          <div
            className={`overflow-hidden transition-all duration-300 ${
              isDashboardExpanded ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            {/* Admin-specific dashboard options */}
            {isAdmin && (
              <>
                <button
                  className="flex items-center w-full px-6 py-2 text-left text-xs rounded-lg transition mt-1"
                  style={{
                    color: 'var(--text-secondary)',
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  onClick={() => navigate("/admin-dashboard/users")}
                >
                  <FaUsers className="mr-2 text-xs" />
                  Manage Users
                </button>
                <button
                  className="flex items-center w-full px-6 py-2 text-left text-xs rounded-lg transition mt-1"
                  style={{
                    color: 'var(--text-secondary)',
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  onClick={() => navigate("/admin-dashboard/analytics")}
                >
                  <FaCogs className="mr-2 text-xs" />
                  AI Analytics
                </button>
              </>
            )}
            
            {/* Regular user dashboard options */}
            {!isAdmin && (
              <>
                <button
                  className="flex items-center w-full px-6 py-2 text-left text-xs rounded-lg transition mt-1"
                  style={{
                    color: 'var(--text-secondary)',
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  onClick={() => navigate("/dashboard/usage")}
                >
                  <FaCogs className="mr-2 text-xs" />
                  AI Usage
                </button>
                <button
                  className="flex items-center w-full px-6 py-2 text-left text-xs rounded-lg transition mt-1"
                  style={{
                    color: 'var(--text-secondary)',
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  onClick={() => navigate("/account")}
                >
                  <FaUser className="mr-2 text-xs" />
                  Profile
                </button>
              </>
            )}
          </div>
        </li>
        )}

        {/* Chatty Section */}
        <li className={`${isOpen ? "opacity-100 w-full" : "hidden pointer-events-none"}`}>
          <div className="flex justify-between items-center w-full px-3 py-2 rounded-md cursor-pointer transition"
            style={{ color: 'var(--text-primary)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}>
            <button
              onClick={() => navigate("/chatty")}
              className="flex-grow text-left flex items-center"
            >
              <FaComments className="mr-2" />
              Chatty
            </button>
            </div>
        </li>

        {/* Agents Section */}
        {user && (
        <li className={`${isOpen ? "opacity-100 w-full" : "hidden pointer-events-none"}`}>
          <div className="flex justify-between items-center w-full px-3 py-2 rounded-md cursor-pointer transition"
            style={{ color: 'var(--text-primary)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}>
            <button
              onClick={() => navigate("/agents")}
              className="flex-grow text-left flex items-center"
            >
              <FaRobot className="mr-2" />
              Agents
            </button>
          </div>
        </li>
        )}

        {/* Original Categories */}
        {categories.map((category, index) => (
          <li
            key={index}
            className={`transition-all min-w-0 ${
              isOpen ? "opacity-100 pl-3 py-2 w-full" : "hidden pointer-events-none"
            }`}
          >
            <button
              onClick={() => {
                navigate(`/${category.toLowerCase()}`);
                if (isMobile) {
                  // Add small delay for mobile to prevent stuck clicks
                  setTimeout(() => {
                    setIsOpen(false);
                  }, 100);
                }
              }}
              className="block w-full text-left px-3 py-2 rounded-md cursor-pointer transition touch-manipulation"
              style={{ color: 'var(--text-primary)' }}
              onMouseEnter={(e) => {
                if (!isMobile) { // Only apply hover effects on non-mobile
                  e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) { // Only apply hover effects on non-mobile
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
              onTouchStart={(e) => {
                if (isMobile) { // Touch feedback for mobile
                  e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                }
              }}
              onTouchEnd={(e) => {
                if (isMobile) { // Reset touch feedback for mobile
                  setTimeout(() => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }, 150);
                }
              }}
            >
              {category}
            </button>
          </li>
        ))}
      </ul>

      
      {/* Clear Chat Confirmation Popup */}
      <ClearChatConfirmation
        isOpen={showClearConfirm}
        onConfirm={handleClearConfirm}
        onCancel={handleClearCancel}
      />
    </aside>
  );
};

// MEMO: Prevent re-renders when popup context changes
export default memo(Sidebar);